<!--
  The SubNavigation component represents a single list
  of navigation links associated to a single parent page/link.

  It features a number of accessibility controls which are responsible
  for managing expand/collapse, keyboard navigation and focus management states.

  props:
    - id: Unique identifier for this SubNavigation instance
    - children: The list of child navigation links associated to its parent
    - menuTitle: The title of its parent menu item
    - menuLink: The link of its parent menu item
    - iconSet: Set of icons to be used in the component
-->
<template>
  <div class="mobile-nav__link-wrapper">
    <!-- MENU LINK -->
    <a :href="menuLink" class="mobile-nav__link">
      {{ menuTitle }}
    </a>

    <!-- BUTTON TO OPEN SUBNAV -->
    <div class="mobile-nav__subnav-btn-wrapper">
      <button
        class="mobile-nav__subnav-btn"
        :class="{ 'mobile-nav__subnav-btn--open' : id === activeNavItem }"
        :aria-label="buttonAriaLabel"
        :aria-controls="'navmenu-subnav-' + id"
        :aria-expanded="id === activeNavItem ? 'true' : 'false'"
        @click="toggleSubNav(id)"
        @keydown.down.prevent="openSubNav(id)"
        @keydown.up.prevent="closeSubNav"
      >
        <img
          :src="icons.chevronMobile"
          alt="Show children pages"
          class="mobile-nav__subnav-btn-icon"/>
      </button>
    </div>
  </div>

  <!-- SUBNAV LIST -->
  <div
    class="mobile-nav__subnav-list-wrapper"
    :class="{ 'mobile-nav__subnav-list-wrapper--open' : id === activeNavItem }"
    :aria-hidden="id === activeNavItem ? 'false' : 'true'"
    :ref="setElementRef"
    :id="dynamicId"
  >
    <div class="mobile-nav__subnav-inner">
      <ul class="mobile-nav__subnav-list">
        <li
          v-for="(item) in children"
          :key="item.ID"
          class="mobile-nav__subnav-item"
        >
          <a :href="item.Link" class="mobile-nav__subnav-link">
            {{ item.MenuTitle }}
          </a>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import { computed, onMounted, ref, toRefs, watch } from 'vue';
import { useKeyboardSubNav } from '../composables';

export default {
  name: "sub-navigation",
  props: {
    /**
     * Unique identifier for this SubNavigation instance.
     */
    id: {
      type: Number,
      required: true,
    },
    /**
     * The list of child navigation links associated to its parent.
     *
     * Sample format:
     * [
     *  ...
     *  {
     *    ID: 1,
     *    Link: '/page-link/',
     *    MenuTitle: 'PageTitle',
     *    Target: null,
     *  }
     *  ...
     * ]
     */
    children: {
      type: Array,
      required: true,
      default: () => [],
    },
    /**
     * The title of its parent menu item.
     */
    menuTitle: {
      type: String,
      required: false,
      default: 'Menu title'
    },
    /**
     * The link of its parent menu item.
     */
    menuLink: {
      type: String,
      required: false,
      default: 'Menu link'
    },
    /**
     * Set of icons to be used inside the component.
     */
    iconSet: {
      type: String,
      required: false,
    }
  },
  setup(props) {
    /**
     * Set up the id and menuTitle references
     */
    const { id, menuTitle } = toRefs(props);
    const icons = JSON.parse(props.iconSet);

    /**
     * This controls the open/close state of a sub navigation item
     * and doesn't impact other items - that is, multiple sub navigation
     * items can be open.
     */
    const activeNavItem = ref(null);

    /**
     * Set up element references
     */
    const elementRefs = ref({});

    /**
     * Create references for accessibility functions from composable component
     */
    const { enableKeyboardNavigation, toggleLinksFocus } = useKeyboardSubNav();

    /**
     * Set reference to the button dynamically
     *
     * @param Element el
     */
    const setElementRef = (el) => {
      elementRefs.value[`navmenu-subnav-${id.value}`] = el;
    };

    /**
     * Calculate the dynamic ID of this subnav
     */
    const dynamicId = `navmenu-subnav-${id.value}`;

    /**
     * Setup a reference for its list element
     */
    const subMenuList = ref(null);

    /**
     * Sets the activeItem to the current item and
     * enables the focus functionality of its links.
     *
     * @param item
     */
    const openSubNav = (item) => {
      activeNavItem.value = item;
      toggleLinksFocus(subMenuList.value, true);
    };

    /**
     * Sets the activeItem to null and disables
     * the focus functionality of its links.
     *
     * @param item
     */
    const closeSubNav = () => {
      activeNavItem.value = null;
      toggleLinksFocus(subMenuList.value, false);
    };

    /**
     * The primary function called by the button element.
     * Responsible for toggling between open and closed states.
     *
     * @param item
     */
    const toggleSubNav = (item) => {
      if (activeNavItem.value === item) {
        closeSubNav();
      } else {
        openSubNav(item);
      }
    };

    /**
     * Add unique label to sub-nav button
     */
    const buttonAriaLabel = computed(() => `Show child pages for ${menuTitle.value}`);

    /**
     * Setup a watcher to listen to the currently
     * selected navigation item and update our local
     * state accordingly.
     */
    watch(activeNavItem, (newValue, oldValue) => {
      // newValue is the new activeNavItem
      // oldValue is the previous activeNavItem
      if (newValue !== oldValue) {
        activeNavItem.value = newValue;
      }
    });

    onMounted(() => {
      // Enable the keyboard functionality for the elements in this SubNav
      const parentLinks = document.querySelectorAll('#mobileNavigation .mobile-nav__link');
      const subMenus = document.querySelectorAll('.mobile-nav__subnav-list-wrapper');
      enableKeyboardNavigation(parentLinks, subMenus);

      // On page load, the SubNav is hidden from view
      // so its links should not be focusable.
      subMenuList.value = elementRefs.value[dynamicId];
      toggleLinksFocus(subMenuList.value, false);
    });

    return {
      activeNavItem,
      dynamicId,
      buttonAriaLabel,
      icons,
      toggleSubNav,
      openSubNav,
      closeSubNav,
      setElementRef,
    };
  },
};
</script>
